type FootnoteAnchor = HTMLElement & {
  dataset: DOMStringMap & {
    qname?: string;
    contextRef?: string;
    fieldLabel?: string;
    fieldName?: string;
    fieldId?: string;
    readonly?: string;
  };
};

type InitOptions = {
  onMarkDirty?: () => void;
  onOpen?: (info: {
    fieldId: string;
    fieldLabel: string;
    fieldName: string;
    contextRef: string;
    qname: string;
    isReadonly: boolean;
  }) => void;
};

// Keep a weak map to avoid duplicate listeners
const processedAnchors = new WeakSet<HTMLElement>();

function buildButton(anchor: FootnoteAnchor, opts: InitOptions) {
  const { qname, contextRef, fieldLabel, fieldName, fieldId } = anchor.dataset;
  if (!qname || !contextRef) return;

  let button = anchor.querySelector('.footnote-edit-btn') as HTMLButtonElement | null;
  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'footnote-edit-btn';
    button.innerHTML = `
      <svg class="w-4 h-4" fill="none" stroke="#333333" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    `;
    anchor.appendChild(button);
  }

  // Sync attributes from anchor to button
  button.setAttribute('data-qname', qname);
  button.setAttribute('data-context-ref', contextRef);
  if (fieldLabel) button.setAttribute('data-field-label', fieldLabel);
  if (fieldName) button.setAttribute('data-field-name', fieldName);
  if (fieldId) button.setAttribute('data-field-id', fieldId);
  if (anchor.dataset.footnoteText) {
    button.setAttribute('data-footnote-text', anchor.dataset.footnoteText || '');
    button.classList.add('has-footnote');
    // Tooltip via title (no React involvement)
    button.title = anchor.dataset.footnoteText || 'Footnote';
  } else {
    button.removeAttribute('data-footnote-text');
    button.classList.remove('has-footnote');
    button.title = 'Click to add footnote';
  }
  if (anchor.dataset.footnoteBreakdown) {
    button.setAttribute('data-footnote-breakdown', anchor.dataset.footnoteBreakdown || '[]');
  } else {
    button.removeAttribute('data-footnote-breakdown');
  }

  if (!processedAnchors.has(anchor)) {
    button.addEventListener('click', () => {
      // ✅ Read data-readonly from anchor at CLICK time (not button creation time)
      // This ensures we always get the current value, even if it was updated after button creation
      const currentReadonly = anchor.dataset.readonly;
      const isReadonly = currentReadonly === 'true';
      
      opts.onOpen?.({
        fieldId: fieldId || `${qname}_${contextRef}`,
        fieldLabel: fieldLabel || qname,
        fieldName: fieldName || qname,
        contextRef,
        qname,
        isReadonly
      });
      opts.onMarkDirty?.();
    });
    processedAnchors.add(anchor);
  }
}

export function refreshFootnoteAnchors(opts: InitOptions = {}) {
  const anchors = document.querySelectorAll('.footnote-anchor') as NodeListOf<FootnoteAnchor>;
  anchors.forEach(anchor => buildButton(anchor, opts));
}

export function initFootnoteAnchors(opts: InitOptions = {}) {
  refreshFootnoteAnchors(opts);
}
